import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  ArrowCheckIcon,
  CancelIcon,
  ClockIcon,
  ErrorFilledIcon,
  SuccessFilledIcon,
} from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import {
  LabelSize,
  LabelVariant,
} from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  ACTION_PLAN_STEP_EXPANSION_KEY,
  ACTION_STEP_TERMINAL_STATUSES,
  ActionStepStatus,
  ActionStepType,
  AiChatPlanStatus,
} from 'constants/aiChat';
import { IconSize } from 'constants/coreDesignSystem';

import { cancelExecutionAction, setActionPlanExpansionAction } from 'store/aiChat/actions';
import {
  selectActionPlanExpansionsByExecutionId,
  selectExecutionPlanByExecutionId,
} from 'store/aiChat/selectors';

import { AiChatTestIds } from '../../AiChat.testIds';
import ActionStep from '../ActionStep';
import ActionStepGroup from '../ActionStepGroup';

import { planStepToActionPlanItem } from './ActionPlan.adapter';
import { ACTION_PLAN_LABELS, actionPlanItemKind } from './ActionPlan.constants';
import {
  actionPlanDepthRowStyles,
  getPlanCardChevronStyles,
  parallelGroupStyles,
  planCardBodyStyles,
  planCardCancelButtonStyles,
  planCardHeaderIconActiveStyles,
  planCardHeaderIconDoneStyles,
  planCardHeaderIconFailedStyles,
  planCardHeaderLeftStyles,
  planCardHeaderRightStyles,
  planCardHeaderStyles,
  planCardHeaderTitleStyles,
  planCardRootStyles,
} from './ActionPlan.styles';
import {
  getPlanProgressCounts,
  groupStepsByDepth,
  isPlanTerminal,
  isVisiblePlanStep,
  resolvePlanHeaderIconVariant,
} from './ActionPlan.utils';

const renderItem = (
  item,
  { expansions, onExpandedChange, onDependencyClick, highlightedStepId },
) => {
  if (item.kind === actionPlanItemKind.GROUP) {
    const { id, label, status, actionStepType, startTime, description, progressText, steps } = item;

    return (
      <ActionStepGroup
        actionStepType={actionStepType}
        description={description}
        key={id}
        label={label}
        progressText={progressText}
        startTime={startTime}
        status={status}
        steps={steps}
      />
    );
  }

  const {
    id,
    title,
    status,
    actionStepType,
    toolName,
    content,
    stepKey,
    isNewSinceReplan,
    error,
    dependsOn,
  } = item;
  const stepExpansions = !_.isNil(stepKey) ? expansions?.[stepKey] : null;
  const expanded = stepExpansions?.[ACTION_PLAN_STEP_EXPANSION_KEY];
  const onChange = onExpandedChange ? value => onExpandedChange(stepKey, value) : null;
  const isHighlighted = !_.isNil(highlightedStepId) ? id === highlightedStepId : null;

  return (
    <ActionStep
      actionStepType={actionStepType}
      content={content}
      dependsOn={dependsOn}
      errorPreview={error}
      expanded={_.isBoolean(expanded) ? expanded : null}
      isHighlightedActive={isHighlighted}
      isNewSinceReplan={isNewSinceReplan}
      key={id}
      status={status}
      stepKey={stepKey}
      title={title}
      toolName={toolName}
      onDependencyClick={onDependencyClick}
      onExpandedChange={onChange}
    />
  );
};

const renderItemsByDepth = (items, renderOptions) => {
  const groups = groupStepsByDepth(items);

  if (groups.length === 0) {
    return null;
  }

  return groups.map(({ depth, steps }) => (
    <Box key={`depth-${depth}`} sx={actionPlanDepthRowStyles}>
      {steps.length > 1 ? (
        <Stack sx={parallelGroupStyles}>{steps.map(item => renderItem(item, renderOptions))}</Stack>
      ) : (
        renderItem(steps[0], renderOptions)
      )}
    </Box>
  ));
};

const resolvePlanHeaderStatusText = (planStatus, progress) => {
  if (planStatus === AiChatPlanStatus.FAILED) {
    return ACTION_PLAN_LABELS.PLAN_FAILED;
  }

  if (planStatus === AiChatPlanStatus.CANCELLED) {
    return ACTION_PLAN_LABELS.PLAN_CANCELLED;
  }

  if (progress.total === 0) {
    return null;
  }

  return ACTION_PLAN_LABELS.PROGRESS(progress);
};

const ActionPlan = ({
  items: itemsProp = null,
  executionId = null,
  loanId = null,
  hasPersistedPlan = false,
}) => {
  const dispatch = useDispatch();
  const plan = useSelector(state => selectExecutionPlanByExecutionId(state, executionId));
  const expansions = useSelector(state =>
    selectActionPlanExpansionsByExecutionId(state, executionId),
  );

  const reduxItems = useMemo(() => {
    if (_.isNil(plan) || !Array.isArray(plan.steps)) {
      return null;
    }

    return plan.steps.filter(isVisiblePlanStep).map(planStepToActionPlanItem);
  }, [plan]);

  const items = useMemo(
    () => (!_.isNil(itemsProp) ? itemsProp : (reduxItems ?? [])),
    [itemsProp, reduxItems],
  );

  const onExpandedChange = useCallback(
    (stepKey, expanded) => {
      if (_.isNil(executionId) || _.isNil(stepKey)) {
        return;
      }

      dispatch(setActionPlanExpansionAction({ executionId, stepKey, expanded }));
    },
    [dispatch, executionId],
  );

  const onDependencyClick = useCallback(stepKey => {
    if (_.isNil(stepKey)) {
      return;
    }

    const target = document.querySelector(`[data-stepkey="${stepKey}"]`);

    target?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, []);

  const planStatus = plan?.status ?? null;
  const isPlanActive = planStatus === AiChatPlanStatus.IN_PROGRESS;
  const planIsTerminal = isPlanTerminal(planStatus);
  const planHeaderIconVariant = resolvePlanHeaderIconVariant(planStatus);

  // Post-refresh, the persisted blob loads asynchronously. We mount with a
  // skeleton (`hasPersistedPlan && !plan`) and the steps hydrate moments later
  // already in a terminal state. To avoid the card opening on first render and
  // collapsing the instant the blob arrives, lock the initial expansion
  // decision: if we mounted in the awaiting-persisted-plan state, default to
  // collapsed for the lifetime of this card.
  const [mountedAwaitingPersistedPlan] = useState(hasPersistedPlan && _.isNil(plan));

  // The plan card is collapsible at the plan level. Default behavior: expand
  // while running, auto-collapse once the plan reaches a terminal state. The
  // user can override at any time by clicking the header — once they do, we
  // honor their preference for the lifetime of the message instance.
  const [userToggle, setUserToggle] = useState(null);
  const autoExpanded = !planIsTerminal && !mountedAwaitingPersistedPlan;
  const isExpanded = _.isBoolean(userToggle) ? userToggle : autoExpanded;

  const onToggleExpansion = useCallback(() => {
    setUserToggle(prev => (_.isBoolean(prev) ? !prev : !autoExpanded));
  }, [autoExpanded]);

  const onHeaderKeyDown = useCallback(
    event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggleExpansion();
      }
    },
    [onToggleExpansion],
  );

  const onCancelClick = useCallback(
    event => {
      event?.stopPropagation?.();

      if (_.isNil(loanId) || _.isNil(executionId)) {
        return;
      }

      dispatch(cancelExecutionAction({ loanId, messageId: executionId }));
    },
    [dispatch, loanId, executionId],
  );

  const canShowCancel = isPlanActive && !_.isNil(loanId) && !_.isNil(executionId);

  const highlightedStep = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) {
      return null;
    }

    const activeSteps = items.filter(
      item =>
        item.kind === actionPlanItemKind.STEP && !ACTION_STEP_TERMINAL_STATUSES.has(item.status),
    );

    return activeSteps.length > 0 ? activeSteps[activeSteps.length - 1] : null;
  }, [items]);

  const highlightedStepId = highlightedStep?.id ?? null;

  const renderableContent = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) {
      return null;
    }

    const renderOptions = { expansions, onExpandedChange, onDependencyClick, highlightedStepId };
    const groupItems = items.filter(item => item.kind === actionPlanItemKind.GROUP);
    const groupedStepIds = new Set(
      groupItems.flatMap(g => (Array.isArray(g.steps) ? g.steps.map(s => s.id) : [])),
    );
    const stepItems = items.filter(
      item => item.kind !== actionPlanItemKind.GROUP && !groupedStepIds.has(item.id),
    );

    if (stepItems.some(item => Array.isArray(item.dependsOn) && item.dependsOn.length > 0)) {
      return (
        <>
          {groupItems.map(item => renderItem(item, renderOptions))}
          {renderItemsByDepth(stepItems, renderOptions)}
        </>
      );
    }

    return items.map(item => renderItem(item, renderOptions));
  }, [items, expansions, onExpandedChange, onDependencyClick, highlightedStepId]);

  // When the message is known to have a persisted plan but the blob hasn't
  // been fetched yet (post-refresh), render the card shell immediately so it
  // appears alongside the rest of the assistant response instead of popping
  // in seconds later. The body stays empty until the steps hydrate.
  const isAwaitingPersistedPlan = hasPersistedPlan && _.isNil(plan);

  // Pre-plan placeholder: the BE fires plan_planning_started at the top of
  // the planner LLM invocation, before plan_created arrives. The reducer
  // stamps `isPlanning=true` on a steps-less plan entry. Render a minimal
  // "Planning..." card so users see immediate feedback during the 5-10s
  // planner window instead of staring at a stalled message bubble.
  const isPlanningPlaceholder =
    !_.isNil(plan) && plan.isPlanning === true && (plan.steps?.length ?? 0) === 0;

  if (
    _.isNil(renderableContent) &&
    _.isNil(planStatus) &&
    !isAwaitingPersistedPlan &&
    !isPlanningPlaceholder
  ) {
    return null;
  }

  const progress = getPlanProgressCounts(plan?.steps);

  const resolveHeaderStatusText = () => {
    if (isPlanningPlaceholder) {
      return ACTION_PLAN_LABELS.PLANNING;
    }

    if (isAwaitingPersistedPlan) {
      return ACTION_PLAN_LABELS.LOADING;
    }

    return resolvePlanHeaderStatusText(planStatus, progress);
  };

  const headerStatusText = resolveHeaderStatusText();

  return (
    <Stack data-testid={AiChatTestIds.ACTION_PLAN_WRAPPER} sx={planCardRootStyles}>
      <Stack
        aria-expanded={isExpanded}
        data-testid={AiChatTestIds.ACTION_PLAN_CARD_HEADER}
        role="button"
        sx={planCardHeaderStyles}
        tabIndex={0}
        onClick={onToggleExpansion}
        onKeyDown={onHeaderKeyDown}
      >
        <Stack sx={planCardHeaderLeftStyles}>
          <Box
            aria-label={isExpanded ? ACTION_PLAN_LABELS.COLLAPSE : ACTION_PLAN_LABELS.EXPAND}
            data-testid={AiChatTestIds.ACTION_PLAN_CARD_TOGGLE}
            sx={getPlanCardChevronStyles(isExpanded)}
          >
            <ArrowCheckIcon size={IconSize[16]} />
          </Box>
          <Box
            sx={
              {
                active: planCardHeaderIconActiveStyles,
                done: planCardHeaderIconDoneStyles,
                failed: planCardHeaderIconFailedStyles,
              }[planHeaderIconVariant]
            }
          >
            {planHeaderIconVariant === 'active' && <ClockIcon size={IconSize[14]} />}
            {planHeaderIconVariant === 'done' && <SuccessFilledIcon size={IconSize[14]} />}
            {planHeaderIconVariant === 'failed' && <ErrorFilledIcon size={IconSize[14]} />}
          </Box>
          <Typography sx={planCardHeaderTitleStyles} variant="medium14">
            {ACTION_PLAN_LABELS.HEADER}
          </Typography>
        </Stack>
        <Stack sx={planCardHeaderRightStyles}>
          {!_.isNil(headerStatusText) && (
            <Box data-testid={AiChatTestIds.ACTION_PLAN_CARD_PROGRESS}>
              <LabelCDS
                withBorder
                size={LabelSize.S}
                title={headerStatusText}
                variant={isPlanActive ? LabelVariant.BLUE : LabelVariant.GRAY}
              />
            </Box>
          )}
          {canShowCancel && (
            <Tooltip title={ACTION_PLAN_LABELS.CANCEL}>
              <IconButton
                aria-label={ACTION_PLAN_LABELS.CANCEL}
                data-testid={AiChatTestIds.ACTION_PLAN_CANCEL_BUTTON}
                size="small"
                sx={planCardCancelButtonStyles}
                onClick={onCancelClick}
              >
                <CancelIcon size={IconSize[16]} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      {isExpanded && !_.isNil(renderableContent) && (
        <Stack data-testid={AiChatTestIds.ACTION_PLAN_CARD_BODY} sx={planCardBodyStyles}>
          {renderableContent}
        </Stack>
      )}
    </Stack>
  );
};

const actionStepItemShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  kind: PropTypes.oneOf([actionPlanItemKind.STEP]).isRequired,
  status: PropTypes.oneOf(Object.values(ActionStepStatus)).isRequired,
  actionStepType: PropTypes.oneOf(Object.values(ActionStepType)),
  content: PropTypes.array,
  dependsOn: PropTypes.arrayOf(PropTypes.string),
  error: PropTypes.string,
  isNewSinceReplan: PropTypes.bool,
  stepKey: PropTypes.string,
  title: PropTypes.string,
  toolName: PropTypes.string,
});

const actionStepGroupItemShape = PropTypes.shape({
  kind: PropTypes.oneOf([actionPlanItemKind.GROUP]).isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  status: PropTypes.oneOf(Object.values(ActionStepStatus)).isRequired,
  actionStepType: PropTypes.oneOf(Object.values(ActionStepType)),
  startTime: PropTypes.string,
  description: PropTypes.string,
  progressText: PropTypes.string,
  steps: PropTypes.array,
});

ActionPlan.propTypes = {
  items: PropTypes.arrayOf(PropTypes.oneOfType([actionStepItemShape, actionStepGroupItemShape])),
  executionId: PropTypes.string,
  loanId: PropTypes.string,
  hasPersistedPlan: PropTypes.bool,
};

export default memo(ActionPlan);
